
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LeadFormData } from "@/types/lead";
import { validateName, validateWhatsApp } from "@/utils/formValidation";
import { MessageCircle } from "lucide-react";

interface PersonalInfoFormProps {
  formData: LeadFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void | Promise<void>;
  isSubmitting: boolean;
}

export const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({
  formData,
  onChange,
  onSubmit,
  isSubmitting
}) => {
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string[]}>({});
  
  const handleFieldBlur = (field: string, value: string) => {
    let validation;
    
    switch (field) {
      case 'name':
        validation = validateName(value);
        break;
      case 'whatsapp':
        validation = validateWhatsApp(value);
        break;
      default:
        return;
    }
    
    setFieldErrors(prev => ({
      ...prev,
      [field]: validation.isValid ? [] : validation.errors
    }));
  };
  
  const hasFieldError = (field: string) => fieldErrors[field]?.length > 0;
  const getFieldError = (field: string) => fieldErrors[field]?.[0];
  
  return (
    <form onSubmit={onSubmit}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome completo</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={onChange}
              onBlur={(e) => handleFieldBlur('name', e.target.value)}
              placeholder="Digite seu nome completo"
              required
              className={`min-h-[44px] ${hasFieldError('name') ? 'border-red-500 focus:border-red-500' : ''}`}
              autoComplete="name"
            />
            {hasFieldError('name') && (
              <p className="text-sm text-red-600 mt-1">{getFieldError('name')}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="whatsapp">WhatsApp</Label>
            <Input
              id="whatsapp"
              name="whatsapp"
              value={formData.whatsapp}
              onChange={onChange}
              onBlur={(e) => handleFieldBlur('whatsapp', e.target.value)}
              placeholder="(11) 99999-9999"
              required
              className={`min-h-[44px] ${hasFieldError('whatsapp') ? 'border-red-500 focus:border-red-500' : ''}`}
              autoComplete="tel"
              maxLength={15}
            />
            {hasFieldError('whatsapp') && (
              <p className="text-sm text-red-600 mt-1">{getFieldError('whatsapp')}</p>
            )}
          </div>
        </div>
        
        <div className="flex justify-end pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full min-h-[48px] bg-[hsl(142,72%,45%)] hover:bg-[hsl(142,72%,38%)] text-white font-semibold text-base"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            {isSubmitting ? 'Processando...' : 'Enviar Mensagem'}
          </Button>
        </div>
      </div>
    </form>
  );
};
